import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Building, FileText, Activity, Shield, Clock, Edit, Power, PowerOff, Key, Crown } from 'lucide-react';
import { fastapiService } from '../../services/fastapiService';
import { WorkspaceApiKeyModal } from '../../components/workspaces/WorkspaceApiKeyModal';

const SUBSCRIPTION_PLANS = ['individual', 'starter', 'business', 'enterprise'];

export const UserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [user, setUser] = useState<any>(null);
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'workspaces' | 'invoices' | 'subscriptions' | 'activity'>('overview');
  const [apiKeyModalWorkspace, setApiKeyModalWorkspace] = useState<{ id: string; name: string } | null>(null);
  const [subscriptionActionLoading, setSubscriptionActionLoading] = useState<string | null>(null);
  const [enrollWorkspaceId, setEnrollWorkspaceId] = useState<string>('');
  const [enrollPlanName, setEnrollPlanName] = useState<string>('individual');

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch user details
      const userRes = await fastapiService.getUserDetail(userId!);
      setUser(userRes.data || userRes);

      // Fetch workspaces
      const workspacesRes = await fastapiService.getUserWorkspaces(userId!);
      setWorkspaces(workspacesRes.data || []);

      // Fetch invoices
      const invoicesRes = await fastapiService.getUserInvoices(userId!);
      setInvoices(invoicesRes.data || []);

      // Fetch subscriptions
      try {
        const subsRes = await fastapiService.getUserSubscriptions(userId!);
        setSubscriptions(subsRes.data || []);
      } catch (_) {
        setSubscriptions([]);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeUserPlan = async (planName: string) => {
    if (!userId) return;
    try {
      setSubscriptionActionLoading('user-plan');
      await fastapiService.changeUserPlan(userId, planName);
      await fetchUserDetails();
    } catch (e: any) {
      alert(e?.error || e?.message || 'Failed to change plan');
    } finally {
      setSubscriptionActionLoading(null);
    }
  };

  const currentUserPlan = subscriptions.length > 0
    ? (subscriptions.find((s: any) => s.status === 'active')?.plan_name || subscriptions[0]?.plan_name)
    : null;

  const handleEnrollInPlan = async () => {
    if (!userId || !enrollWorkspaceId) return;
    try {
      setSubscriptionActionLoading('enroll');
      await fastapiService.enrollUserInPlan(userId, enrollWorkspaceId, enrollPlanName);
      setEnrollWorkspaceId('');
      setEnrollPlanName('individual');
      await fetchUserDetails();
    } catch (e: any) {
      alert(e?.error || e?.message || 'Failed to enroll in plan');
    } finally {
      setSubscriptionActionLoading(null);
    }
  };

  const handleToggleStatus = async () => {
    if (!user) return;
    try {
      // TODO: Call toggle status endpoint
      await fetchUserDetails();
    } catch (e: any) {
      setError(e?.message || 'Failed to update user status');
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/admin/users')}
          className="flex items-center space-x-2 text-admin-600 hover:text-admin-900"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Users</span>
        </button>
        <div className="admin-card p-6">
          <div className="text-red-600">{error || 'User not found'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/users')}
            className="flex items-center space-x-2 text-admin-600 hover:text-admin-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-admin-900">{user.full_name || user.email}</h1>
            <p className="mt-1 text-sm text-admin-600">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleToggleStatus}
            className={`admin-button flex items-center space-x-2 px-4 py-2 ${
              user.is_active ? 'admin-button-secondary' : 'admin-button-primary'
            }`}
          >
            {user.is_active ? (
              <>
                <PowerOff className="w-4 h-4" />
                <span>Deactivate</span>
              </>
            ) : (
              <>
                <Power className="w-4 h-4" />
                <span>Activate</span>
              </>
            )}
          </button>
          <button className="admin-button-secondary flex items-center space-x-2 px-4 py-2">
            <Edit className="w-4 h-4" />
            <span>Edit</span>
          </button>
        </div>
      </div>

      {/* Status Badge */}
      <div className="admin-card p-4">
        <div className="flex items-center space-x-4">
          <div>
            <span className="text-xs font-medium text-admin-500 uppercase">Status</span>
            <div className="mt-1">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                user.is_active ? 'bg-green-100 text-green-700' : 'bg-admin-100 text-admin-700'
              }`}>
                {user.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
          <div>
            <span className="text-xs font-medium text-admin-500 uppercase">Role</span>
            <div className="mt-1">
              <span className={`px-3 py-1 rounded text-sm font-medium ${
                user.role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                user.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {user.role || 'user'}
              </span>
            </div>
          </div>
          <div>
            <span className="text-xs font-medium text-admin-500 uppercase">Workspaces</span>
            <div className="mt-1 text-sm font-medium text-admin-900">{workspaces.length}</div>
          </div>
          <div>
            <span className="text-xs font-medium text-admin-500 uppercase">Invoices</span>
            <div className="mt-1 text-sm font-medium text-admin-900">{invoices.length}</div>
          </div>
          <div>
            <span className="text-xs font-medium text-admin-500 uppercase">Subscriptions</span>
            <div className="mt-1 text-sm font-medium text-admin-900">{subscriptions.length}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-card">
        <div className="border-b border-admin-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview', icon: Mail },
              { id: 'workspaces', label: 'Workspaces', icon: Building },
              { id: 'subscriptions', label: 'Subscriptions', icon: Crown },
              { id: 'invoices', label: 'Invoices', icon: FileText },
              { id: 'activity', label: 'Activity', icon: Activity },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-admin-500 hover:text-admin-700 hover:border-admin-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="text-xs font-medium text-admin-500 uppercase">Email</label>
                  <p className="mt-1 text-sm text-admin-900">{user.email || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-admin-500 uppercase">Full Name</label>
                  <p className="mt-1 text-sm text-admin-900">{user.full_name || user.first_name || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-admin-500 uppercase">Phone</label>
                  <p className="mt-1 text-sm text-admin-900">{user.phone || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-admin-500 uppercase">Created At</label>
                  <p className="mt-1 text-sm text-admin-900">
                    {user.created_at ? formatDate(user.created_at) : '-'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-admin-500 uppercase">Last Updated</label>
                  <p className="mt-1 text-sm text-admin-900">
                    {user.updated_at ? formatDate(user.updated_at) : '-'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-admin-500 uppercase">User ID</label>
                  <p className="mt-1 text-sm font-mono text-admin-900">{user.id}</p>
                </div>
              </div>

              {/* Additional Details */}
              <div className="border-t border-admin-200 pt-6">
                <h3 className="text-sm font-medium text-admin-900 mb-4">Additional Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-admin-500 uppercase">Email Verified</label>
                    <p className="mt-1 text-sm text-admin-900">
                      {user.email_verified ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-admin-500 uppercase">Workspace Count</label>
                    <p className="mt-1 text-sm text-admin-900">{workspaces.length}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-admin-500 uppercase">Total Invoices</label>
                    <p className="mt-1 text-sm text-admin-900">{invoices.length}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-admin-500 uppercase">Paid Invoices</label>
                    <p className="mt-1 text-sm text-admin-900">
                      {invoices.filter((inv: any) => inv.status === 'paid').length}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-admin-500 uppercase">Active Subscriptions</label>
                    <p className="mt-1 text-sm text-admin-900">
                      {subscriptions.filter((s: any) => s.status === 'active').length} of {subscriptions.length}
                    </p>
                  </div>
                </div>
              </div>
              {subscriptions.length > 0 && (
                <div className="border-t border-admin-200 pt-6">
                  <h3 className="text-sm font-medium text-admin-900 mb-3">Subscription Plans</h3>
                  <div className="space-y-2">
                    {subscriptions.slice(0, 5).map((sub: any) => (
                      <div key={sub.id} className="flex justify-between text-sm">
                        <span className="text-admin-700">{sub.workspace?.name || sub.workspace_id}</span>
                        <span className={`font-medium capitalize ${sub.status === 'active' ? 'text-green-600' : 'text-admin-600'}`}>
                          {sub.plan_name} ({sub.status})
                        </span>
                      </div>
                    ))}
                    {subscriptions.length > 5 && (
                      <button
                        onClick={() => setActiveTab('subscriptions')}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        View all {subscriptions.length} subscriptions →
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'subscriptions' && (
            <div className="space-y-6">
              <h3 className="text-sm font-medium text-admin-900">User&apos;s Plan (applies to all workspaces)</h3>

              {subscriptions.length === 0 ? (
                <p className="text-sm text-admin-500 py-4">No subscriptions found for this user.</p>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border border-admin-200 rounded-lg bg-admin-50">
                    <div>
                      <p className="font-medium text-admin-900">
                        Current plan: <span className="capitalize">{currentUserPlan || 'None'}</span>
                      </p>
                      <p className="text-sm text-admin-600 mt-1">
                        {subscriptions.filter((s: any) => s.status === 'active').length} active subscription(s) across {subscriptions.length} workspace(s)
                      </p>
                      {subscriptions[0]?.current_period_end && (
                        <p className="text-xs text-admin-500 mt-1">
                          Period ends: {formatDate(subscriptions[0].current_period_end)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-admin-500">Upgrade/Downgrade:</span>
                      <select
                        value={currentUserPlan || 'individual'}
                        onChange={(e) => handleChangeUserPlan(e.target.value)}
                        disabled={subscriptionActionLoading === 'user-plan'}
                        className="text-sm border border-admin-300 rounded-md px-3 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        title="Change user's plan (applies to all workspaces)"
                      >
                        {SUBSCRIPTION_PLANS.map((p) => (
                          <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                        ))}
                      </select>
                      {subscriptionActionLoading === 'user-plan' && (
                        <span className="text-xs text-admin-500">Updating...</span>
                      )}
                    </div>
                  </div>

                  {subscriptions.length > 1 && (
                    <details className="text-sm text-admin-600">
                      <summary className="cursor-pointer hover:text-admin-700">Workspaces with this plan</summary>
                      <ul className="mt-2 space-y-1 pl-4">
                        {subscriptions.map((sub: any) => (
                          <li key={sub.id}>
                            {sub.workspace?.name || sub.workspace_id} — {sub.plan_name} ({sub.status})
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}

              <div className="border-t border-admin-200 pt-6">
                <h4 className="text-sm font-medium text-admin-900 mb-3">Enroll in Plan</h4>
                <p className="text-sm text-admin-600 mb-4">Enroll this user in a subscription plan for a workspace.</p>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="block text-xs font-medium text-admin-500 mb-1">Workspace</label>
                    <select
                      value={enrollWorkspaceId}
                      onChange={(e) => setEnrollWorkspaceId(e.target.value)}
                      className="text-sm border border-admin-300 rounded-md px-3 py-2 min-w-[200px] focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select workspace</option>
                      {workspaces.map((ws: any) => (
                        <option key={ws.id} value={ws.id}>{ws.name || ws.id}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-admin-500 mb-1">Plan</label>
                    <select
                      value={enrollPlanName}
                      onChange={(e) => setEnrollPlanName(e.target.value)}
                      className="text-sm border border-admin-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-primary-500"
                    >
                      {SUBSCRIPTION_PLANS.map((p) => (
                        <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={handleEnrollInPlan}
                    disabled={!enrollWorkspaceId || subscriptionActionLoading === 'enroll'}
                    className="admin-button-primary px-4 py-2 text-sm disabled:opacity-50"
                  >
                    {subscriptionActionLoading === 'enroll' ? 'Enrolling...' : 'Enroll'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'workspaces' && (
            <div className="space-y-4">
              {workspaces.length === 0 ? (
                <p className="text-sm text-admin-500 py-4">No workspaces found</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-admin-200">
                    <thead className="bg-admin-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Role</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Joined</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-admin-200">
                      {workspaces.map((ws: any) => (
                        <tr key={ws.id} className="hover:bg-admin-50">
                          <td className="px-4 py-3 text-sm font-medium text-admin-900">{ws.name || '-'}</td>
                          <td className="px-4 py-3 text-sm text-admin-700">
                            <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">
                              {ws.user_role || ws.role || 'member'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              ws.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-admin-100 text-admin-700'
                            }`}>
                              {ws.is_active !== false ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-admin-500">
                            {ws.joined_at ? formatDate(ws.joined_at) : 
                             ws.created_at ? formatDate(ws.created_at) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => setApiKeyModalWorkspace({ id: ws.id, name: ws.name || 'Workspace' })}
                                className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700"
                                title="Manage API key overrides"
                              >
                                <Key className="h-4 w-4" /> API Keys
                              </button>
                              <button
                                onClick={() => navigate(`/admin/workspaces/${ws.id}`)}
                                className="text-primary-600 hover:text-primary-700"
                              >
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'invoices' && (
            <div className="space-y-4">
              {invoices.length === 0 ? (
                <p className="text-sm text-admin-500 py-4">No invoices found</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-admin-200">
                    <thead className="bg-admin-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Invoice #</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Workspace</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Created</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-admin-200">
                      {invoices.map((inv: any) => (
                        <tr key={inv.id} className="hover:bg-admin-50">
                          <td className="px-4 py-3 text-sm font-mono text-admin-900">
                            {inv.invoice_number || `INV-${inv.id.substring(0, 8).toUpperCase()}`}
                          </td>
                          <td className="px-4 py-3 text-sm text-admin-700">
                            {inv.workspaces?.name || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-admin-900">
                            {formatCurrency(parseFloat(inv.amount || 0))}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                              inv.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-admin-100 text-admin-700'
                            }`}>
                              {inv.status || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-admin-500">
                            {inv.created_at ? formatDate(inv.created_at) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <button
                              onClick={() => navigate(`/admin/invoices/${inv.id}`)}
                              className="text-primary-600 hover:text-primary-700"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-4">
              <p className="text-sm text-admin-500 py-4">Activity log coming soon</p>
            </div>
          )}
        </div>
      </div>

      {apiKeyModalWorkspace && (
        <WorkspaceApiKeyModal
          workspaceId={apiKeyModalWorkspace.id}
          workspaceName={apiKeyModalWorkspace.name}
          isOpen={!!apiKeyModalWorkspace}
          onClose={() => setApiKeyModalWorkspace(null)}
        />
      )}
    </div>
  );
};

